/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  GamoraControllerStateArgs,
  GamoraControllerStateMap,
  debugCreate,
} from '@ver.id/libs.internal.gamora';
import {
  HoruzProtocolChulakStateBase,
  HoruzProtocolChulakStateBaseRecvResult,
} from '../base.js';
import { HoruzProtocolChulakClientStateContext } from '../context.js';
import { HoruzProtocolChulakDataType } from '../../data.js';
import { HoruzProtocolChulakMessage } from '../../guards/index.js';
import { HoruzProtocolChulakMessageModule } from '../../modules/message.js';
import { IHoruz } from '../../../../horuz.js';
const debug = debugCreate({
  namespace: 'horuz:protocols:chulak:states:client:base',
});
/**
 * @public
 */
export type HoruzProtocolChulakClientStateReplayMessageInput = [
  HoruzProtocolChulakDataType,
  (
    stateContext: HoruzProtocolChulakClientStateContext,
    message: any,
  ) => Promise<void>,
];

/**
 * HoruzProtocolChulakClientState
 *
 * Abstract class that represents the state node
 * @internal
 */
export class HoruzProtocolChulakClientStateBase<
  TStateMap extends GamoraControllerStateMap,
  TStateContext extends HoruzProtocolChulakClientStateContext,
  TRoot extends IHoruz,
> extends HoruzProtocolChulakStateBase<TStateMap, TStateContext, TRoot> {
  constructor(args: GamoraControllerStateArgs) {
    super(args);
  }

  /**
   * Replays messages in state in order
   * @param clientStateContext Context
   * @param inputs The inputs
   */
  async replayMessagesInState(
    clientStateContext: HoruzProtocolChulakClientStateContext,
    inputs: HoruzProtocolChulakClientStateReplayMessageInput[],
  ) {
    let hasExecuted = false;
    for (const [type, executor] of inputs) {
      if (
        clientStateContext.stateContextRegistry.localServiceSession!.hasDataByType(
          type,
        )
      ) {
        const message = HoruzProtocolChulakMessageModule.Decode(
          clientStateContext.stateContextRegistry.localServiceSession!.getDataByType(
            type,
          ),
        );

        // Execute message invoker
        await executor(clientStateContext, message);
        hasExecuted = true;
      } else {
        break;
      }
    }
    return hasExecuted;
  }

  /**
   * Try to handle exception
   * @param stateContext
   */
  protected _tryHandleException(serverStateContext: TStateContext, e?: any) {
    debug.trace(e?.message ?? 'Unknown exception occurred.', e?.stack);
    this._tryAbortContextToAbort(serverStateContext);
  }

  /**
   * Try to abort context if active
   * @param stateContext
   */
  protected _tryAbortContextToAbort = (serverStateContext: TStateContext) => {
    if (this.$isInCurrentState(serverStateContext)) {
      this.$controller.$tryAbortContext(serverStateContext.contextIdentifier, {
        stateAbort: 'Abort',
      });
    }
  };

  /**
   * Receive message
   * @param stateContext
   */
  override recv(
    stateContext: TStateContext,
  ): HoruzProtocolChulakStateBaseRecvResult {
    const receiveResult = super.recv(stateContext);
    if (receiveResult.done === false) {
      stateContext.stateContextRegistry.localServiceSession?.setData(
        receiveResult.message.type,
        HoruzProtocolChulakMessageModule.Encode(receiveResult.message),
      );
    }

    return receiveResult;
  }

  /**
   * Send message
   * @param stateContext
   */
  override async send(
    stateContext: TStateContext,
    message: HoruzProtocolChulakMessage,
  ): Promise<void> {
    const data = HoruzProtocolChulakMessageModule.Encode(message);

    // Encode
    stateContext.ws.send(data);

    stateContext.stateContextRegistry.localServiceSession?.setData(
      message.type,
      data,
    );
  }
}
